import { useQuery } from '@tanstack/react-query'
import type { GraphQLResult } from 'aws-amplify/api'
import { productAdapter } from '@skop-app/lib-domain/adapters'
import { filterBuilder } from '@skop-app/lib-search'
import type { QueryParams } from '@skop-app/lib-search'
import type { SearchProductInput } from '@skop-app/lib-search'
import {
  LocationInput,
  SearchProductSortInput,
  SearchProductsQuery,
} from '@skop-app/lib-search'

import { getAmplifyClient } from '@/services/amplifyClient'
import { searchProductV2PublicQuery } from '@/services/graphql/publicSearch'

const EXCLUDED_PUBLIC_SHOP_ID = '8604990a-6c8e-4623-9aed-472c9e756e08'

type PublicSearchV2Filter = Omit<
  SearchProductInput,
  'nature' | 'availableForSale' | 'batchedProductID'
> & {
  actionValues?: { eqs?: string[] }
}

const buildPublicSearchV2Filter = (
  queryParams: QueryParams,
  saleActions: string[],
): PublicSearchV2Filter => {
  const builtFilter = filterBuilder(queryParams, {
    progress: { eq: 'active' },
    slug: { ne: 'null' },
    and: [{ slug: { exists: true } }],
  }) as SearchProductInput & { actionValues?: { eqs?: string[] } }

  const {
    nature: _nature,
    availableForSale: _available,
    batchedProductID: _batch,
    action,
    ...filter
  } = builtFilter
  const selectedActions = action?.eq ? [action.eq] : saleActions

  return {
    ...filter,
    ...(selectedActions.length > 0
      ? { actionValues: { eqs: [...new Set(selectedActions)] } }
      : {}),
    and: [...(filter.and ?? []), { shopID: { ne: EXCLUDED_PUBLIC_SHOP_ID } }],
  }
}

// Types are slightly different from the original code
type SearchParams = {
  location?: {
    lat: number
    lon: number
  }
  queryParams: QueryParams
  saleActions?: string[]
  sort?: SearchProductSortInput
  limit?: number
  nextToken?: number
}

export const search = async (params: SearchParams) => {
  const {
    location: locationParam,
    queryParams,
    saleActions = [],
    sort,
    limit,
    nextToken,
  } = params

  const location: LocationInput | undefined = locationParam?.lat
    ? {
        lat: locationParam?.lat,
        lon: locationParam?.lon,
      }
    : undefined

  const { products, total } = await searchProducts({
    location,
    distance: location?.lat ? 10000 : undefined,
    filter: buildPublicSearchV2Filter(queryParams, saleActions),
    sort,
    limit,
    nextToken,
  })

  return { products, total }
}

type SearchResults = {
  searchProductV2?: {
    items?: any[] | null
    total?: number | null
    nextToken: string
  } | null
}

const searchProducts = async ({
  location,
  distance,
  filter,
  sort,
  limit,
  nextToken,
}: Omit<SearchProductsQuery, 'filter'> & {
  filter?: PublicSearchV2Filter
  nextToken?: number
}) => {
  const client = getAmplifyClient()
  const result = (await client.graphql({
    query: searchProductV2PublicQuery,
    variables: { location, distance, filter, sort, limit, nextToken },
  })) as GraphQLResult<SearchResults>

  if (result.errors?.length) {
    throw new Error('Search V2 is unavailable')
  }

  const searchResult = result.data?.searchProductV2
  if (
    !searchResult ||
    !Array.isArray(searchResult.items) ||
    typeof searchResult.total !== 'number'
  ) {
    throw new Error('Search V2 is unavailable')
  }

  return {
    products: searchResult.items.map(productAdapter),
    total: searchResult.total,
  }
}

export const useSearchProducts = (filter) => {
  const scope = filter?.variables ?? filter
  const hasScope = Boolean(
    scope?.filter?.byUserID?.eq ||
    scope?.filter?.organizationID?.eq ||
    scope?.filter?.shopID?.eq,
  )
  return useQuery({
    queryKey: ['searchProducts', filter],
    queryFn: async () => await searchProducts(filter),
    enabled: hasScope,
    placeholderData: { products: [], total: 0 },
  })
}
